// Angular
import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
} from "@angular/core";

//d3
import { select, tree, hierarchy, linkHorizontal, text } from "d3";

//rxjs
import { fromEvent } from "rxjs";
import { debounceTime, map, throttleTime } from "rxjs/operators";

//c3
import * as c3 from "c3";
import { A, AA, B, C, CR, MAIN, NCR } from "src/app/app.contant";

@Component({
    selector: 'app-tree-chart',
    templateUrl: './tree-chart.component.html',
    styleUrls: ['./tree-chart.component.scss'],
    standalone: false
})
export class TreeChartComponent implements OnChanges, AfterViewInit {
	height: number;
	width: number;
	treeData: any;
	svg: any;
	linkPathGenerator: any;
	treeMap: any;
	links: any;
	rootNode: any;
	group: any;
	lines: any;
	pathLinks: any;
	circleLinks: any;
	textLinks: any;
	rectangleCreateData: any[] = [];
	classifications = [AA, A, B, C, CR, NCR];
	rectangleMakerList = ['pp', 'ppk', 'cp', 'cpk'];
	infinitySymbol = "--";
	currentToolTipCircle: any;
	mainPlotElement: HTMLElement;
	mainPlotElemRect: ClientRect;
	gNodeRect: ClientRect;
	mainGNodeElem: any;
	svgTreeInitialElem: any;
	mainContentElem: any;
	mouseCoordinates: { x: number; y: number } = { x: 0, y: 0 };
	@Input() updatedTreeData: any;
	@Input() from: string = MAIN;
	@Output() plotScroll = new EventEmitter();
	totalchildNode: number;
	// tracking mouse move on mainplot element to handle tooltip hiding
	@ViewChild("mainPlot") set content(content: ElementRef) {
		if (content) {
			this.mainPlotElement = content.nativeElement;
			this.mainPlotElemRect = this.mainPlotElement.getBoundingClientRect();
			this.renderTreeSvg();
			fromEvent(this.mainPlotElement, "mousemove")
				?.pipe(
					map(this.setMouseCoordinatesforMachineTooltip),
					debounceTime(100)
				)
				?.subscribe(this.handleTooltipMouseLeave);
		}
	}

	ngAfterViewInit(): void {
		// mat-autocomplete scrolling issue temporary fix
		const mc = document.querySelectorAll(".main-content")[0];
		const o = mc && mc[0] ? mc[0] : null;
		if (!o) {
			return;
		}
		fromEvent(o, 'scroll')?.pipe(throttleTime(1000))?.subscribe(this.sendScrollEvent);
	}

	sendScrollEvent = (data: any) => {
		this.plotScroll.emit(data);
	};

	// setting mouse coordinate values for tooltip positioning
	setMouseCoordinatesforMachineTooltip = (mapEvent: any) => {
		if (mapEvent) {
			this.mouseCoordinates = { x: mapEvent.x, y: mapEvent.y };
			return mapEvent;
		}
	};

	// handle tooltip hiding
	handleTooltipMouseLeave = (e: any) => {
		if (e?.target?.localName !== "text" || e?.target?.localName === "svg") {
			this.mouseleave();
		}
		//issue wihth tooltip somtimes its not hiding on mouse change
		setTimeout(() => {
			this.mouseleave();
		}, 20000);
	};

	// reset variables required for svg rendering, rerender svg based on data received
	renderTreeSvg() {
		select("body").selectAll(".svg-tree-diagram").remove();
		this.rectangleCreateData = [];
		this.buildNewSvg();
		this.parentFunction(this.updatedTreeData);
		this.setMargintoSvg();
	}

	// track data for drawing tree
	ngOnChanges(changes: SimpleChanges): void {
		this.rectangleMakerList = this.from === 'main' ? this.rectangleMakerList : ['pp', 'ppk'];
		if (this.updatedTreeData?.children?.length > 0) {
			this.renderTreeSvg();
		}
	}

	// setting variables for drawing d3 tree
	parentFunction(data: any) {
		this.rootNode = hierarchy(data, (d: any) => d.children);
		this.pathLinks = this.rootNode.links();
		this.circleLinks = this.rootNode.descendants();
		this.totalchildNode = 0;
		// calculate total number of child node
		this.updatedTreeData?.children.forEach(e => {
			e.children.forEach(e1 => {
				if (e1.children) {
					this.totalchildNode = this.totalchildNode + e1.children.length;
				}
			});
		});
		this.textLinks = this.rootNode.descendants();
		this.linkPathGenerator = linkHorizontal()
			.x(this.setXattributeForCirclesOrPath)
			.y(this.setYattributeForCirclesOrPath);
		this.treeMap = tree().nodeSize([10, 100]).separation(this.nodeSeperation);
		this.treeData = this.treeMap(this.rootNode);
		this.createTreeElementsAndSetPosition();
	}

	// setting node seperation value
	nodeSeperation = () => 4;

	// creating d3 tree,adding nodes,text and positioning main container svg
	createTreeElementsAndSetPosition() {
		this.createTree();
		this.addCircle();
		this.addThresholdValuesText();
		this.setContainerSvgDimAndPosition();
	}

	// setting container svg dimensions based on the d3 library tree path height
	setContainerSvgDimAndPosition() {
		this.mainGNodeElem =
			document.getElementsByClassName("tree-outer-g-node") || {};
		this.gNodeRect = this.mainGNodeElem[0]?.getBoundingClientRect() || {};
		this.svgTreeInitialElem =
			document.querySelectorAll(".svg-tree-diagram") || {};
		this.mainContentElem = document.querySelectorAll(".main-content") || {};
		if (
			this.mainGNodeElem &&
			this.gNodeRect &&
			this.mainPlotElement &&
			this.mainPlotElemRect &&
			this.svgTreeInitialElem &&
			this.mainContentElem
		) {

			//adjusting width of svg tree structure
			this.svgTreeInitialElem[0]?.setAttribute(
				"width",
				this.mainPlotElemRect.width -
				0.05 * this.mainPlotElemRect.width
			);


			let svgHeight: any;
			svgHeight = 180 + (40 * this.totalchildNode);
			this.svgTreeInitialElem[0]?.setAttribute("height", svgHeight);
			this.mainGNodeElem[0]?.setAttribute(
				"transform",
				`translate(${0.04 * this.mainPlotElement.getBoundingClientRect().width
				},${0.5 * svgHeight})`
			);

			//code for scroll bar to the bottom
			// this.mainPlotElement?.scrollTo({
			// 	top: svgHeight / 2.5,
			// 	behavior: "smooth",
			// });
			// this.mainContentElem[0]?.scrollTo({
			// 	top: svgHeight / 2.5,
			// 	behavior: "smooth",
			// });
		}
	}

	// building and initializing an svg container for accomodating d3 tree
	buildNewSvg() {
		this.svg = select("#main-plot")
			.append("svg")
			.style("position", "absolute")
			.style("top", "0")
			.style("left", "0")
			.attr("class", "svg-tree-diagram");
	}

	// creating paths with input data received using d3 tree
	createTree() {
		this.group = this.svg
			.append("g")
			.attr("class", "tree-outer-g-node")
			.attr("transform", "translate(50)");
		this.lines = this.group
			.selectAll("g")
			.data(this.pathLinks)
			.enter()
			.append("g")
			.attr("class", "node")
			.attr("d", this.linkPathGenerator);

		this.lines
			.append("path")
			.attr("fill", "none")
			.attr("stroke", "#A9A9A9")
			.attr("d", this.linkPathGenerator);
	}

	// handling circle radiuses on different nodes.
	addCircleRadius = (d: any) => {
		let radius: number;
		switch (d.depth) {
			case 0:
				radius = 28;
				break;
			case 1:
				radius = 25;
				break;
			case 2:
				radius = this.from === MAIN ? 14 : 18;
				break;
			default:
				radius = 0;
				break;
		}
		return radius;
	};

	// adding dynamic class names for circles at different nodes
	addCircleClasses = (d: any) => {
		return `circle-${d.data.id}`;
	};

	// setting different colours for circles ar different nodes
	fillCircleColours = (d: any) => {
		let fill: string;
		if (this.classifications.includes(d.data.name)) {
			switch (d.data.name) {
				case this.classifications[0]:
					fill = "#E55761";
					break;
				case this.classifications[1]:
					fill = "#FF8C02";
					break;
				case this.classifications[2]:
					fill = "#008CBC";
					break;
				case this.classifications[3]:
					fill = "#893499";
					break;
				case this.classifications[4]:
					fill = "#E55761";
					break;
				case this.classifications[5]:
					fill = "#FF8C02";
					break;
			}
		} else {
			switch (d.depth) {
				case 0:
					fill = "#4F6984";
					break;
				case 1:
					fill = "#4F5984";
					break;
				default:
					fill = "none";
					break;
			}
		}
		return fill;
	};

	// adding circle at nodes
	addCircle() {
		this.group
			.selectAll("circle")
			.data(this.circleLinks)
			.enter()
			.append("circle")
			.attr("cx", this.setXattributeForCirclesOrPath)
			.attr("cy", this.setYattributeForCirclesOrPath)
			.attr("r", this.addCircleRadius)
			.attr("class", this.addCircleClasses)
			.attr("cursor", "pointer")
			.attr("fill", this.fillCircleColours)
			.attr("stroke", "#A9A9A9")
			.on("click", this.collapseOnClickEvent);
		this.addText();
	}

	// setting classes for different texts dynamically
	addTextClasses = (d: any) => {
		if (!d.children) {
			return `param${d.data.parameterId}`;
		}
		if (d?.depth === 1) {
			return `machine-${d.data.id}-${d.data.name}`;
		}
	};

	// adding texts for different nodes
	addText() {
		this.group
			.selectAll("text")
			.data(this.circleLinks)
			.enter()
			.append("text")
			.attr("x", this.setYattributeForText)
			.attr("y", this.setXattributeForText)
			.attr("dy", "0.32em")
			.attr("text-anchor", this.setTextAnchor)
			.attr("cursor", "pointer")
			.attr("font-family", "RobotoMedium, Serif, Sans-serif, cursive, fantasy, Monospace")
			.attr("font-size", "13")
			.attr("class", this.addTextClasses)
			.attr("display", "block")
			.text(this.setTextValue)
			.on("mousemove", this.textMouseMoveEvent)
			.on("click", this.collapseOnClickEvent)
			.filter(this.textFilter)
			.append("title")
			.text(this.textTitleContent);
	}

	setXattributeForCirclesOrPath = (d: any) => d.y;

	setYattributeForCirclesOrPath = (d: any) => d.x;

	setYattributeForText = (d: any) => (d.children ? d.y : d.y + 2.5);

	setXattributeForText = (d: any) => d.x;

	setTextAnchor = (d: any) => (d.children ? "middle" : "start");

	setTextValue = (d: any) => {
		if (!d.data.children) {
			this.rectangleCreateData.push(d);
		}
		if (d?.data.name && d?.children && !this.classifications.includes(d.data.name)) {
			return `${d.data.name.substring(0, 3)}...`;
		} else if (d?.data.name && d?.children && this.classifications.includes(d.data.name)) {
			return d.data.name;
		} else {
			if (d.data.name) {
				return d?.data.name?.length > 18 ? `${d.data.name.substring(0, 18)}...` : d.data.name;
			}
			return '...';
		}
	};

	textMouseMoveEvent = (d: any, index: number, element: any) => {
		if (this.from === 'quality') {
			// return;
		}
		this.mouseleave();
		if (d.depth === 1) {
			this.currentToolTipCircle = element.find(e => e.attributes.class?.nodeValue === `machine-${d.data.id}-${d.data.name}`);
			this.mouseMoveOverMachine(d);
		} else if (d.depth === 2) {
			this.mouseMoveOverClassification(d);
		} else if (d.depth === 3) {
			this.mouseMoveOverparameter(d);
		}
	};

	collapseOnClickEvent = (d: any) => {
		d?.children && this.collapse(d);
	};

	textFilter = (d: any) => {
		return this.from === MAIN ? d.depth !== 1 && d.depth !== 2 : true;
	};

	textTitleContent = (d: any) => {
		if (d.data && d.data.name && d.parent == null) {
			return d.data.name;
		}
	};

	// finding parameters under ceratain classifications
	findParametersUnderClassification(data: any, classificationType: string) {
		return (
			data.find((data: any) => data.name === classificationType)?.children
				?.length || 0
		);
	}
	mouseMoveOverparameter(d: any) {
		const left = this.svgTreeInitialElem && this.svgTreeInitialElem[0]?.getBoundingClientRect().left + d.y;

		select("body")
			.append("div")
			.attr("class", "donut-tooltip")
			.style("opacity", 1)
			.style("position", "absolute")
			.style("left", `${left + 100}px`)
			.style("top", `${this.mouseCoordinates.y - 50}px`)
			.html(
				`<table style='border: solid 1px #A9A9A9; border-radius:5px;color:white; background:black;'>
					<tr>
					<td style='padding: 7px;'>
					${d.data.name}
					</td>
					</tr>
				</table>`);
	};
	// handle tooltip show, based on mouse move for circle's texts at machine nodes (level 2)
	mouseMoveOverMachine(d: any) {
		const f: boolean = this.from === MAIN;
		const classificationAA = this.findParametersUnderClassification(d.data.children, f ? 'AA' : NCR);
		const classificationA = this.findParametersUnderClassification(d.data.children, f ? 'A' : CR);
		const classificationB = this.findParametersUnderClassification(d.data.children, 'B');
		const classificationC = this.findParametersUnderClassification(d.data.children, 'C');
		const total = classificationAA + classificationA + classificationB + classificationC;
		const left = this.svgTreeInitialElem && this.svgTreeInitialElem[0]?.getBoundingClientRect().left + d.y;
		select("body")
			.append("div")
			.attr("class", "donut-tooltip")
			.style("opacity", 1)
			.style("position", "absolute")
			.style("left", `${left + 70}px`)
			.style("top", `${this.mouseCoordinates.y}px`)
			.html(
				`<table style='border: solid 1px #A9A9A9; border-radius:5px; background:white;'>
					<tr>
					<td>
						<div
						id='donut-machine-name' style='display:flex;justify-content:center;
						font-family: Roboto, "Helvetica Neue", sans-serif;
						opacity: 0.8; margin-top:.5rem; font-weight: bold;font-size: 13px;'>${d.data.name}</div>
						<div id='donut-machine-name'
						style='display:flex;justify-content:center;
						font-family: Roboto, "Helvetica Neue", sans-serif;
						opacity: 0.8; font-weight: bold;font-size: 12px;color:#004976;'>Total ${f ? 'Parameters' : 'Characters'}: ${total}</div>
						<div id='donut-tooltip-container' class="donut-tooltip-container" style='height:130px;width:160px;'>					
						</div>
					</td>
					</tr>
				</table>`);

		setTimeout(() => {
			const donutContainer = document.getElementById("donut-tooltip-container");
			let columns = [
				['data1', classificationAA], ['data2', classificationA],
				['data3', classificationB], ['data4', classificationC]
			];
			const names = { data1: f ? AA : NCR, data2: f ? A : CR, data3: B, data4: C };
			columns = f ? columns : columns.splice(0, 2);
			const obj: any = {
				bindto: donutContainer,
				legend: { show: true },
				data: {
					columns,
					names,
					type: "donut",
					colors: {
						data1: "#E55761", data2: "#FF8C02",
						data3: "#008CBC", data4: "#893499"
					}
				},
				donut: {
					label: { show: false }, width: 10, expand: true
				},
				transition: { duration: 2000 }
			};
			const chart = c3.generate(obj);
		}, 0);
	}

	// handle tooltip show, based on mouse move for circle's texts at machine nodes (level 3)
	mouseMoveOverClassification(d: any) {
		const f: boolean = this.from === MAIN;
		const left =
			this.svgTreeInitialElem &&
			this.svgTreeInitialElem[0]?.getBoundingClientRect().left + d.y;
		const parameterList = d?.children?.map((data: any) => data?.data);
		const cpA = this.classifyThresholdValues(parameterList, 'cp', A)?.length;
		const cpB = this.classifyThresholdValues(parameterList, 'cp', B)?.length;
		const cpC = this.classifyThresholdValues(parameterList, 'cp', C)?.length;
		const cpD = this.classifyThresholdValues(parameterList, 'cp', 'D')?.length;
		const cpE = this.classifyThresholdValues(parameterList, 'cp', 'E')?.length;

		const cpkA = this.classifyThresholdValues(parameterList, 'cpk', A)?.length;
		const cpkB = this.classifyThresholdValues(parameterList, 'cpk', B)?.length;
		const cpkC = this.classifyThresholdValues(parameterList, 'cpk', C)?.length;
		const cpkD = this.classifyThresholdValues(parameterList, 'cpk', 'D')?.length;
		const cpkE = this.classifyThresholdValues(parameterList, 'cpk', 'E')?.length;

		const ppA = this.classifyThresholdValues(parameterList, 'pp', A)?.length;
		const ppB = this.classifyThresholdValues(parameterList, 'pp', B)?.length;
		const ppC = this.classifyThresholdValues(parameterList, 'pp', C)?.length;
		const ppD = this.classifyThresholdValues(parameterList, 'pp', 'D')?.length;
		const ppE = this.classifyThresholdValues(parameterList, 'pp', 'E')?.length;

		const ppkA = this.classifyThresholdValues(parameterList, 'ppk', A)?.length;
		const ppkB = this.classifyThresholdValues(parameterList, 'ppk', B)?.length;
		const ppkC = this.classifyThresholdValues(parameterList, 'ppk', C)?.length;
		const ppkD = this.classifyThresholdValues(parameterList, 'ppk', 'D')?.length;
		const ppkE = this.classifyThresholdValues(parameterList, 'ppk', 'E')?.length;

		select("body")
			.append("div")
			.attr("class", "donut-tooltip")
			.style("opacity", 1)
			.style("position", "absolute")
			.style("left", `${left + 60}px`)
			.style("top", `${this.mouseCoordinates.y}px`)
			.html(
				`<table 
					style='
					border: solid .5px #A9A9A9;
					background:white;'
					<tr><td>
					<div class="spacing"></div>
					<div
					id='bar-tooltip-container'
					class="bar-tooltip-container"
					style='
					height:250px;
					width:425px;
					padding-bottom:10px;
					'>
				</div></td></tr>
				</table>`
			);
		setTimeout(() => {
			const donutContainer = document.getElementById("bar-tooltip-container");
			let columns: any = [
				['cp', cpA, cpB, cpC, cpD, cpE],
				['cpk', cpkA, cpkB, cpkC, cpkD, cpkE],
				['pp', ppA, ppB, ppC, ppD, ppE],
				['ppk', ppkA, ppkB, ppkC, ppkD, ppkE],
			];
			columns = f ? columns : columns.splice(2, 2);
			const chart = c3.generate({
				bindto: donutContainer,
				size: {
					height: this.height,
				},
				data: {
					columns,
					type: 'bar',
					colors: {
						cp: "#4472C5",
						cpk: "#ED7E30",
						pp: "#A5A5A5",
						ppk: "#FFC100",
					},
				},
				bar: {
					width: { ratio: 0.75 },
				},
				axis: {
					x: {
						type: 'category',
						categories: [
							"< 0.67",
							"0.67 - 1",
							"1 - 1.33",
							"1.33 - 1.67",
							"> 1.67",
						],
					},
				},
				legend: {
					show: true,
				},
				tooltip: {
					format: {
						title: (d) => "Criticality",
					},
				},
			});
		}, 0);
	}

	// categorising data for plot the chart
	classifyThresholdValues(zoneData: any[], type: string, criticality: string) {
		// null values are coming from API which is considered as a good value (>=1.67)
		// setting those value as 2 to plot the graph
		zoneData?.forEach((znDtaEle: any) => {
			if (znDtaEle[type] === null) {
				znDtaEle[type] = 2;
			}
		});
		let chartList: any;
		switch (criticality) {
			case "A":
				chartList = zoneData?.filter((znDtaEle: any) => {
					return znDtaEle[type] < 0.67;
				});
				break;
			case "B":
				chartList = zoneData?.filter((znDtaEle: any) => {
					return znDtaEle[type] >= 0.67 && znDtaEle[type] < 1;
				});
				break;
			case "C":
				chartList = zoneData?.filter((znDtaEle: any) => {
					return znDtaEle[type] >= 1 && znDtaEle[type] < 1.33;
				});
				break;
			case "D":
				chartList = zoneData?.filter((znDtaEle: any) => {
					return znDtaEle[type] >= 1.33 && znDtaEle[type] < 1.67;
				});
				break;
			case "E":
				chartList = zoneData?.filter((znDtaEle: any) => {
					return znDtaEle[type] >= 1.67;
				});
				break;
		}
		return chartList;
	}

	// removing tooltip on leaving mouse away from a particular node
	mouseleave() {
		select("body").selectAll("div.donut-tooltip").remove();
	}

	// adding rectangles, texts for threshold values(pp,ppk,cp,cpk)
	addThresholdValuesText() {
		const f: boolean = this.from === MAIN;
		for (const [i, data] of this.rectangleCreateData.entries()) {
			for (const [j, dataEle] of this.rectangleMakerList.entries()) {
				let thresholdValue =
					data.data[this.rectangleMakerList[j]] !== null
						? Number(data.data[this.rectangleMakerList[j]])
						: null;
				let thresholdTitle = this.rectangleMakerList[j];
				this.group
					.append("rect")
					.attr("class", this.setClassesForThresholdRectangles(data))
					.attr("x", this.setXattributeForThresholdRectangles(data, j))
					.attr("y", data.x - 7.5)
					.attr("width", f ? 70 : 80)
					.attr("height", 20)
					.attr("cursor", "pointer")
					.attr("rx", 2)
					.attr("fill", this.fillColorsForThresholdRectangles(thresholdValue));

				this.group
					.append("text")
					.attr("class", this.setClassesForRectangleContentText(data))
					.attr("x", this.setXattributesForRectangleContentText(data, j))
					.attr("y", data.x)
					.attr("dy", "0.32em")
					.attr("font-weight", "700")
					.attr("font-size", "13")
					.attr("cursor", "pointer")
					.attr("dominant-baseline", "middle")
					.attr("text-anchor", "middle")
					.append("tspan")
					.attr("y", data.x)
					.text(this.setTextValueForRectangleContentText(thresholdValue, thresholdTitle))
					.append("title")
					.text(this.setThresholdValueTitleContent(thresholdValue, thresholdTitle));
			}
		}
		this.enLargeInfinitySymbol();
	}

	// enlarge infinity symbol
	enLargeInfinitySymbol() {
		Array.from(document.querySelectorAll("tspan"))
			.filter((testElem: any) => testElem.textContent.includes("--"))
			.forEach(this.changeInnerHTML);
	}

	//enlarging inifnity symbol by editing innerHTML
	changeInnerHTML = (infinityElem: any) => {
		infinityElem.innerHTML = `<tspan fill='grey'>${infinityElem.innerHTML.split(":")[0]
			} : </tspan>
    <tspan  style='font-size:13px;' fill='grey'>${this.infinitySymbol}</tspan>
    <title>${infinityElem.innerHTML.split(":")[0]} : ${this.infinitySymbol
			}<title>`;
		return infinityElem;
	};

	//setting class name dynamically for the threshold rectangles
	setClassesForThresholdRectangles = (data: any) => {
		return `rect${data.data.id}`;
	};

	//setting x attributes for the threshold rectangles
	setXattributeForThresholdRectangles = (data: any, j: number) => {
		const text = document.querySelector(`.param${data.data.parameterId}`);
		let w = 1;
		if (text) {
			const width = text.getBoundingClientRect().width;
			w = this.from === MAIN ? j * 75 + data.y + width + 20 : j * 85 + data.y + width + 45;
			return w;
		}
	};

	//setting x attributes for the threshold rectangles text content
	setXattributesForRectangleContentText = (data: any, j: number) => {
		const text = document.querySelector(`.param${data.data.parameterId}`);
		const rect: any = document.querySelector(`.rect${data.data.id}`);
		let width = rect ? rect.getBoundingClientRect().width : 1;
		let x = 1;
		if (rect) {
			width = rect.getBoundingClientRect().width;
			x = rect.attributes.x ? parseFloat(rect.attributes.x.value) : 1;
		}
		if (text) {
			const gap: number = this.from === MAIN ? 75 : 85;
			return (j * gap) + x + width / 2;
		}
	};

	// fill rectangle colors based on the criticality
	fillColorsForThresholdRectangles = (thresholdValue: number) => {
		let fill: string;
		if (thresholdValue === null) {
			fill = "#B2BEB5";
		} else {
			if (thresholdValue < 0.67) {
				fill = "#ED4343";
			} else if (thresholdValue >= 0.67 && thresholdValue < 1) {
				fill = "#FF8900";
			} else if (thresholdValue >= 1 && thresholdValue < 1.33) {
				fill = "#FFBA00";
			} else if (thresholdValue >= 1.33 && thresholdValue < 1.67) {
				fill = "#BBC300";
			} else if (thresholdValue >= 1.67) {
				fill = "#0FBE52";
			}
		}
		return fill;
	};

	//setting classes dynamically for the threshold contents
	setClassesForRectangleContentText = (data: any) => {
		return `sub-text-${data.data.id}`;
	};

	//setting text content for the threshold rectangles
	setTextValueForRectangleContentText = (
		thresholdValue: number,
		thresholdTitle: string
	) => {
		let text: any;
		if (thresholdValue === null || thresholdValue > 50) {
			text = `${thresholdTitle} : ${this.infinitySymbol}`;
		} else {
			text = `${thresholdTitle} : ${thresholdValue}`;
		}
		if (this.from === MAIN) {
			return text.length > 9 ? `${text.substring(0, 9)}...` : text;
		}
		return text.length > 9 ? `${text.substring(0, 11)} ..` : text;
	};

	//setting text content for the threshold rectangles content's tooltip
	setThresholdValueTitleContent = (thresholdValue: number, thresholdTitle: string) => {
		let text: any;
		if (thresholdValue === null || thresholdValue > 50) {
			text = `${thresholdTitle} : ${this.infinitySymbol}`;
		} else {
			text = `${thresholdTitle} : ${thresholdValue}`;
		}
		return text;
	};

	// handle circle click, collapse/expand childs
	collapse(d: any) {
		this.collapseChild(d);
		this.circleTranstion(d);
		this.pathTransition(d);
		this.textTransition(d);
		this.rectangleTransition(d);
		this.subTextTransition(d);
	}

	// handle child collapse/expand recursively
	collapseChild(d: any) {
		if (d.data.isCollapseChild === false) {
			d.data.isCollapseChild = true;
			if (d.data.children) {
				this.collapseChildren(d.data.children);
			}
		} else {
			d.data.isCollapseChild = false;
		}
	}

	// handle child collapse/expand recursively
	collapseChildren(data: any) {
		data?.forEach((ele: any) => {
			ele.isCollapseChild = true;
			if (ele.children) {
				this.collapseChildren(ele.children);
			}
		});
	}

	// handle circle transitions on click
	circleTranstion(d: any) {
		d.children.forEach((ele: any, i: number) => {
			if (d.data.isCollapseChild === true) {
				this.svg
					.selectAll(`.circle-${d.data.id}${i + 1}`)
					.transition()
					.duration(1000)
					.attr("cx", d.y)
					.attr("cy", d.x)
					.attr("r", 0);
				if (ele.children) {
					this.circleTranstion(ele);
				}
			} else {
				this.svg
					.selectAll(`.circle-${d.data.id}${i + 1}`)
					.transition()
					.duration(1000)
					.attr("cx", ele.y)
					.attr("cy", ele.x)
					.attr("r", this.resetCircleRadius(d, i));
				if (ele.children) {
					this.circleTranstion(ele);
				}
			}
		});
	}

	// handling restting of circle radiuses on different nodes.
	resetCircleRadius = (d: any, i: number) => {
		let radius: number;
		switch (d.children[i].depth) {
			case 0:
				radius = 28;
				break;
			case 1:
				radius = 25;
				break;
			case 2:
				radius = 14;
				break;
			default:
				radius = 0;
				break;
		}
		return radius;
	};

	// handle path transitions on click
	pathTransition(d: any) {
		if (d.children) {
			const line = this.svg.selectAll("path").filter((ele: any) => {
				return ele.source.x === d.x && ele.source.y === d.y;
			});
			d.children.forEach((ele: any, i: number) => {
				if (d.data.isCollapseChild === true) {
					line
						.transition()
						.delay(200)
						.duration(500)
						.attr("opacity", 0)
						.attr("display", "none");
					this.pathTransition(ele);
				} else {
					line
						.transition()
						.delay(200)
						.duration(500)
						.attr("opacity", 1)
						.attr("display", "block");
					this.pathTransition(ele);
				}
			});
		}
	}

	// handle texts transitions on click
	textTransition(d: any) {
		if (d.children) {
			const text = this.svg.selectAll("text").filter((ele: any) => {
				if (ele && ele.data) {
					return d.children.find((somEle: any) => {
						return ele.data.id === somEle.data.id;
					});
				}
			});
			d.children.forEach((ele: any, i: number) => {
				if (d.data.isCollapseChild === true) {
					text
						.transition()
						.delay(200)
						.duration(500)
						.attr("opacity", 0)
						.attr("display", "none");
					this.textTransition(ele);
				} else {
					text
						.transition()
						.delay(200)
						.duration(500)
						.attr("opacity", 1)
						.attr("display", "block");
					this.textTransition(ele);
				}
			});
		}
	}

	// handle rectangle transitions on click
	rectangleTransition(d: any) {
		if (d.children) {
			d.children.forEach((ele: any, i: number) => {
				if (d.data.isCollapseChild === true) {
					this.svg
						.selectAll(`.rect${d.data.id}${i + 1}`)
						.transition()
						.delay(200)
						.duration(1000)
						.attr("opacity", 0)
						.attr("display", "none");
					this.rectangleTransition(ele);
				} else {
					this.svg
						.selectAll(`.rect${d.data.id}${i + 1}`)
						.transition()
						.delay(200)
						.duration(1000)
						.attr("opacity", 1)
						.attr("display", "block");
					this.rectangleTransition(ele);
				}
			});
		}
	}

	// handle subTexts(pp,ppk,cp,cpk) transitions on click
	subTextTransition(d: any) {
		if (d.children) {
			d.children.forEach((ele: any, i: number) => {
				if (d.data.isCollapseChild === true) {
					this.svg
						.selectAll(`.sub-text-${d.data.id}${i + 1}`)
						.transition()
						.delay(200)
						.duration(1000)
						.attr("opacity", 0)
						.attr("display", "none");
					this.subTextTransition(ele);
				} else {
					this.svg
						.selectAll(`.sub-text-${d.data.id}${i + 1}`)
						.transition()
						.delay(200)
						.duration(1000)
						.attr("opacity", 1)
						.attr("display", "block");
					this.subTextTransition(ele);
				}
			});
		}
	}

	public setMargintoSvg(): void {
		try {
			const g: any = this.svg.select('g.tree-outer-g-node');
			const text: any = this.svg.select('text[class^="param"]');
			if (!g || !text) {
				return;
			}
			let y: any = text.attr('y');
			y = parseFloat(y);
			if (y >= 0) {
				return;
			}
			y = Math.abs(y);
			const transform: string = g.attr('transform');
			const arr: Array<string> = transform.split(',');
			arr[1] = (y + 10) + ')';
			g.attr('transform', arr.join(','));
			let height = this.svg.attr('height');
			height = parseFloat(height);
			this.svg.attr('height', height - 150);
		} catch (e) { }
	}
}
